import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import {
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  Box,
  Typography,
  Avatar,
  TextField,
  CircularProgress,
} from "@mui/material";
import {
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import {
  Sidebar,
  Menu,
  MenuItem,
  SubMenu,
  useProSidebar,
} from "react-pro-sidebar";
import {
  Menu as MenuIcon,
  SpaceDashboard as SpaceDashboardTwoToneIcon,
  EditNote as EditNoteIcon,
  DeleteForever as DeleteForeverIcon,
  Chat as ChatIcon,
  Settings as SettingsIcon,
  Folder as FolderIcon,
  DiamondOutlined as DiamondOutlinedIcon,
  FolderOff as FolderOffIcon,
  AdminPanelSettings as AdminPanelSettingsTwoToneIcon,
  Summarize as SummarizeIcon,
  HelpOutline as HelpOutlineIcon,
  AddCircle as AddCircleIcon,
  SupervisorAccount as SupervisorAccountIcon,
  Refresh as RefreshIcon,
  ChangeCircle as ChangeCircleIcon,
  BatchPrediction as BatchPredictionIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  BiotechRounded as BiotechIcon,
  HubRounded as HubIcon,
  MonitorHeartRounded as MonitorHeartIcon,
  ScienceRounded as ScienceIcon,
  KeyboardArrowRight as ChevronRightIcon,
  BarChart as BarChartIcon,
} from "@mui/icons-material";
import SettingsAccessibilityIcon from '@mui/icons-material/SettingsAccessibility';
import { Tooltip } from "@mui/material";
import AppLogoLightMode from "src/assets/images/drugvlab_logo_light_mode.svg";
import { useAppDispatch, useAppSelector } from "src/store/app/hooks";
import { closeDrawer, deleteDrawerItem } from "src/store/features/drawerSlice";
import { openDrawer, setDrawerWidth } from "src/store/features/drawerSlice";
import { useTranslation } from "react-i18next";
import { openAvatarMenu } from "src/store/features/appbarSlice";
import AvatarMenu from "./AvatarMenu";
import { getCheckAdminAPI } from "src/store/features/admin/checkAdminSlice";
import {
  listLibraryAPI,
  removeLibraryFromList,
} from "src/store/features/library/libraryListSlice";
import { renameLibraryAPI } from "src/store/features/library/renameLibrarySlice";
import { deleteLibraryAPI } from "src/store/features/library/deleteLibrarySlice";
import { wholeWorkflowAPI } from "src/store/features/dashboard/wholeWorkflowSlice";
import {
  setLibrary,
  setShowNextRoundAssayModal,
  setUserChoiceTool,
  setUserChoiceToolType,
  setUserHaveAssayFile,
} from "src/store/features/ppi/ppi-user-parameters/ppiUserParametersSlice";
import { setCommonRoundNum } from "src/store/features/user/commonUserParameterSlice";
import { getUserMyCreditBalanceAPI } from "src/store/features/user/credit/userMyCreditBalanceSlice";
import { setUserDrugResponseChoiceTool } from "src/store/features/drug-response/drug-response-user/drugResponseUserParameterSlice";
import { getDjangoEndpoint } from "src/utils/apiEndpoints";
import MakeWorkSpace from "../ppi-inhibitor/ppi-inhibitor-round1/slider-components/step1/MakeWorkSpace";
import NextRoundMoveModal from "./modal/NextRoundMoveModal";
import { preloadRouteForPath } from "src/router/routeLoaders";
import axiosInstance from "src/store/features/auth/axiosCSRF";


// 모달 애니메이션 효과
const MODAL_ANIMATIONS = `
  @keyframes modalIn {
    from { opacity: 0; transform: translateY(16px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes iconPop {
    0%   { transform: scale(0.5); opacity: 0; }
    65%  { transform: scale(1.15); opacity: 1; }
    100% { transform: scale(1); opacity: 1; }
  }
  @keyframes fadeSlide {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

// 배경화면 스타일
const BACKDROP_PROPS = {
  sx: { backgroundColor: "rgba(15,23,42,0.4)", backdropFilter: "blur(8px)" },
};

const preloadLinkedRoute = (event: React.SyntheticEvent) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const anchor = target.closest<HTMLAnchorElement>("a[href]");
  if (!anchor || anchor.origin !== window.location.origin) return;
  preloadRouteForPath(anchor.pathname);
};

/*  TOKENS 스타일 */
const TOKENS = {
  bg: "#f6f8fb",
  bgSubtle: "#ffffff",
  bgMuted: "#edf2f7",
  border: "rgba(203,213,225,0.72)",
  borderStrong: "#cbd5e1",
  text: "#111827",
  textSub: "#475569",
  textMuted: "#7c8796",
  accent: "#254f87",
  accentLight: "rgba(37,79,135,0.1)",
  accentMid: "rgba(37,79,135,0.18)",
  success: "#10b981",
  warning: "#f59e0b",
  danger: "#ef4444",
  gold: "#f7ac34",
};

/*  BUTTON STYLES */
const cancelBtnSx = {
  borderRadius: "10px",
  borderColor: TOKENS.borderStrong,
  color: TOKENS.textSub,
  fontWeight: 600,
  fontSize: "0.82rem",
  py: 1.2,
  px: 2.5,
  textTransform: "none",
  "&:hover": {
    borderColor: "#cbd5e1",
    background: TOKENS.bgSubtle,
    color: TOKENS.text,
  },
};

// primary 버튼 스타일
const primaryBtnSx = {
  borderRadius: "10px",
  background: TOKENS.accent,
  color: "#fff",
  fontWeight: 700,
  fontSize: "0.82rem",
  py: 1.2,
  px: 2.5,
  textTransform: "none",
  boxShadow: `0 2px 8px ${TOKENS.accentMid}`,
  transition: "all 0.15s ease",
  "&:hover": {
    background: "#3a569a",
    boxShadow: "0 4px 14px rgba(73,104,162,0.35)",
    transform: "translateY(-1px)",
  },
  "&:disabled": { background: "#e5e7eb", color: "#9ca3af" },
};
const dangerBtnSx = {
  borderRadius: "10px",
  background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
  color: "#fff",
  fontWeight: 700,
  fontSize: "0.82rem",
  py: 1.2,
  px: 2.5,
  textTransform: "none",
  boxShadow: "0 2px 8px rgba(239,68,68,0.2)",
  transition: "all 0.15s ease",
  "&:hover": {
    background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
    transform: "translateY(-1px)",
    boxShadow: "0 6px 16px rgba(239,68,68,0.3)",
  },
  "&:disabled": { background: "#e5e7eb", color: "#9ca3af" },
};

/*  MODAL SUB COMPONENTS*/
const ModalDivider = () => (
  <Box sx={{ px: 3.5 }}>
    <Box sx={{ height: "1px", background: TOKENS.border }} />
  </Box>
);

// 아이콘 뱃지 스타일
const IconBadge = ({ bgColor, borderColor, children, mounted }: any) => (
  <Box
    sx={{
      width: 44,
      height: 44,
      borderRadius: "14px",
      background: bgColor,
      border: `1.5px solid ${borderColor}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      mb: 1.5,
      animation: mounted
        ? "iconPop 0.4s cubic-bezier(0.34,1.56,0.64,1) 0.1s both"
        : "none",
    }}
  >
    {children}
  </Box>
);

// 카드 고르기 스타일
const ChoiceCard = ({
  onClick,
  hoverBorderColor,
  hoverBgColor,
  iconBg,
  Icon,
  title,
  description,
}: any) => (
  <Box
    component="button"
    onClick={onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      padding: "16px 18px",
      border: `1.5px solid ${TOKENS.borderStrong}`,
      borderRadius: "14px",
      backgroundColor: TOKENS.bg,
      cursor: "pointer",
      width: "100%",
      textAlign: "left",
      transition: "all 0.2s cubic-bezier(0.4,0,0.2,1)",
      "&:hover": {
        borderColor: hoverBorderColor,
        backgroundColor: hoverBgColor,
        transform: "translateY(-2px)",
        boxShadow: `0 8px 24px -4px ${hoverBorderColor}2a`,
      },
      "&:hover .card-arrow": { transform: "translateX(5px)", opacity: 1 },
      "&:active": { transform: "translateY(0px)" },
    }}
  >
    <Box
      sx={{
        width: 46,
        height: 46,
        borderRadius: "12px",
        background: iconBg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mr: 2,
        flexShrink: 0,
      }}
    >
      <Icon sx={{ color: "white", fontSize: "20px" }} />
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        sx={{
          fontWeight: 700,
          color: TOKENS.text,
          fontSize: "0.9rem",
          mb: 0.25,
          lineHeight: 1.3,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{ color: TOKENS.textMuted, fontSize: "0.78rem", lineHeight: 1.5 }}
      >
        {description}
      </Typography>
    </Box>
    <Box
      className="card-arrow"
      sx={{
        opacity: 0.3,
        transition: "all 0.2s ease",
        color: TOKENS.textMuted,
        flexShrink: 0,
        ml: 1.5,
      }}
    >
      <ArrowForwardIcon sx={{ fontSize: "16px" }} />
    </Box>
  </Box>
);

/*  WORKFLOW CONFIG */
const WORKFLOW_CONFIG: Record<
  string,
  { color: string; bg: string; label: string; Icon: any }
> = {
  "PPI Inhibitor": {
    color: "#4968A2",
    bg: "rgba(73,104,162,0.1)",
    label: "P",
    Icon: HubIcon,
  },
  "Kinase Inhibitor": {
    color: "#f7ac34",
    bg: "rgba(247,172,52,0.1)",
    label: "K",
    Icon: BiotechIcon,
  },
  "Drug Response": {
    color: "#10b981",
    bg: "rgba(16,185,129,0.1)",
    label: "D",
    Icon: MonitorHeartIcon,
  },
  "Drug Toxicity": {
    color: "#C00000",
    bg: "rgba(192,0,0,0.08)",
    label: "T",
    Icon: ScienceIcon,
  },
  Biomarker: {
    color: "#7c3aed",
    bg: "rgba(124,58,237,0.1)",
    label: "B",
    Icon: BiotechIcon,
  },
  MDTR: {
    color: "#4968A2",
    bg: "rgba(73,104,162,0.1)",
    label: "M",
    Icon: SettingsAccessibilityIcon,
  },
};

/* MAIN COMPONENT */
function MainDrawer({
  onNextStep,
  Open = true,
  showAssayModal = false,
  selectedTool = null,
  selectedDrugResponseMode = null,
  modalOnly = false,
}) {
  // 기본 훅. 라우팅, 번역, Redux dispatch, URL query를 사이드바 상태와 연결.
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const queryList = [...searchParams];
  const { collapseSidebar } = useProSidebar();

  // 스크롤 참조. 현재 워크스페이스와 열린 라운드 목록을 화면 안으로 보정.
  const libraryItemRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const subMenuRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const sidebarScrollRef = useRef<HTMLDivElement | null>(null);

  // 상태 관리. Drawer 표시, 선택 항목, 모달, 리사이즈 값을 한 곳에서 관리.
  const [isOpen, setOpen] = useState(false);
  const [itemName, setItemName] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [pageId, setPageId] = useState(null);
  const [modalType, setModalType] = useState<
    "running-checking" | "success" | "loading"
  >("loading");
  const [runningCheckAction, setRunningCheckAction] = useState<
    "delete" | "rename"
  >("delete");
  const [runningModal, setRunningModal] = useState(false);
  const [showPPIModal, setShowPPIModal] = useState(false);
  const [modalKey, setModalKey] = useState(0);
  const [isCreatingLibrary, setIsCreatingLibrary] = useState(false);
  const [showAssayChoiceModal, setShowAssayChoiceModal] = useState(false);
  const [openedLibrary, setOpenedLibrary] = useState<string | null>(null);
  const [showDrugResponseModal, setShowDrugResponseModal] = useState(false);
  const [showDrugToxicityModal, setShowDrugToxicityModal] = useState(false);
  const [selectedDrugToxicityType, setSelectedDrugToxicityType] = useState<
    "mdtr" | "fatetox" | "animal" | null
  >(null);
  const [showMdtrModal, setShowMdtrModal] = useState(false);
  const [showNextRoundModal, setShowNextRoundModal] = useState(false);
  const [selectedLibraryForNextRound, setSelectedLibraryForNextRound] =
    useState(null);
  const [nextRoundNumber, setNextRoundNumber] = useState(null);
  const [dialogType, setDialogType] = useState("chat");
  const [editedLibraryName, setEditedLibraryName] = useState("");
  const [renameError, setRenameError] = useState("");
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartWidth, setResizeStartWidth] = useState(240);

  const collapsedWidth = 64;
  const expandedWidth = 248;
  const minWidth = 200;
  const maxWidth = 600;

  /* ── Redux 상태. 사용자/권한/워크스페이스/크레딧/실행 현황을 조회. ── */
  const itemList = useAppSelector((s) => s.drawer.itemList);
  const drawerWidth = useAppSelector((s) => s.drawer.drawerWidth);
  const library = useAppSelector((s) => s?.userParameter?.library);
  const showNextRoundAssayModal = useAppSelector(
    (s) => s?.userParameter?.showNextRoundAssayModal
  );
  const myCredit = useAppSelector(
    (s) => s?.userMyCreditBalance?.response?.credit
  );
  const email = useAppSelector((s) => s.userInfo?.response?.name);
  const isAdmin = useAppSelector((s) => s.checkAdmin.response?.is_admin);
  const listLibrary = useAppSelector((s) => s?.listLibrary?.response);
  const deleteLoading = useAppSelector((s) => s?.deleteLibrary?.isLoading);
  const renameLoading = useAppSelector((s) => s?.renameLibrary?.isLoading);
  const dashboardWholeWorkflow = useAppSelector((s) => s?.dashboardHistory?.response);
  const userChoiceSelectTool = useAppSelector((s) => s?.userParameter?.userChoiceTool);
  const userChoiceDrugResponseSelectToolType = useAppSelector((s) => s?.userParameter?.userChoiceToolType);

  const avatarColors = [
    "#4968A2",
    "#10b981",
    "#f7ac34",
    "#ef4444",
    "#8b5cf6",
    "#06b6d4",
    "#f59e0b",
    "#ec4899",
  ];
  // 아바타 색상. 사용자 이름 기반 해시로 고정 색상을 선택.
  const getAvatarColor = (str: string) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++)
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    return avatarColors[Math.abs(hash) % avatarColors.length];
  };
  const userInitial = email?.charAt(0).toUpperCase() ?? "U";

  /* ── 초기 데이터. App에서 사용자·크레딧을 조회하므로 Drawer 고유 데이터만 로드. ── */
  useEffect(() => {
    if (modalOnly) return;
    if (showNextRoundAssayModal) {
      handleWithAssayFile();
      dispatch(setShowNextRoundAssayModal(false));
    }
  }, [showNextRoundAssayModal, dispatch, modalOnly]);

  // 실제 Drawer 최초 진입 시 필요한 권한·워크스페이스 상태를 가져옴.
  useEffect(() => {
    if (modalOnly) return;
    dispatch(getCheckAdminAPI());
    dispatch(listLibraryAPI({ group_name: "temp_group" }));
    dispatch(wholeWorkflowAPI());
  }, [dispatch, modalOnly]);

  // 라운드 동기화. 현재 URL의 round 값을 공통 Redux 상태에 반영.
  useEffect(() => {
    if (modalOnly) return;
    const match = location.pathname.match(/\/round(\d+)/);
    dispatch(
      setCommonRoundNum({ round_num: match?.[1] ? parseInt(match[1], 10) : 1 })
    );
  }, [location.pathname, dispatch, modalOnly]);

  // 활성 메뉴. prompt history 라우트의 id query를 읽어 선택 상태를 표시.
  useEffect(() => {
    if (modalOnly) return;
    setPageId(null);
    for (const item of queryList) {
      if (item[0] === "id") setPageId(item[1]);
    }
  }, [location.pathname, modalOnly]);

  // CSS 변수. Drawer 폭을 body 변수로 내려 레이아웃 계산에 사용.
  useEffect(() => {
    if (modalOnly) return;
    document.body.style.setProperty(
      "--drawer-width",
      isCollapsed ? `${collapsedWidth}px` : `${expandedWidth}px`
    );
  }, [isCollapsed, modalOnly]);

  /* ── Drawer 리사이즈. 드래그 시작 위치와 시작 폭을 저장. ── */
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeStartX(e.clientX);
    setResizeStartWidth(drawerWidth);
  };
  
  // Drawer 리사이즈 완료. mouseup 시 최종 폭을 min/max 안으로 제한해 Redux에 저장.
  useEffect(() => {
    if (!isResizing) return;
    const onMove = (e: MouseEvent) => {
      e.preventDefault();
    };
    const onUp = (e: MouseEvent) => {
      e.preventDefault();
      const nw = Math.min(
        Math.max(resizeStartWidth + (e.clientX - resizeStartX), minWidth),
        maxWidth
      );
      dispatch(setDrawerWidth(nw));
      setIsResizing(false);
    };
    document.addEventListener("mousemove", onMove, { capture: true });
    document.addEventListener("mouseup", onUp, { capture: true });
    return () => {
      document.removeEventListener("mousemove", onMove, { capture: true });
      document.removeEventListener("mouseup", onUp, { capture: true });
    };
  }, [isResizing, resizeStartX, resizeStartWidth, dispatch]);

  // 리사이즈 UX. 드래그 중 커서와 텍스트 선택을 잠금.
  useEffect(() => {
    if (modalOnly) return;
    document.body.style.cursor = isResizing ? "col-resize" : "";
    document.body.style.userSelect = isResizing ? "none" : "";
  }, [isResizing, modalOnly]);

  /* ── 스크롤 보정. 현재 워크스페이스/서브메뉴가 화면 안에 보이도록 이동. ── */
  useEffect(() => {
    if (modalOnly) return;
    const match = location.pathname.match(
      /\/(?:ppi-inhibitor|kinase-inhibitor|drug-response)\/([^/]+)/
    );
    if (!match) return;
    const active = decodeURIComponent(match[1]);
    const timer = window.setTimeout(() => {
      const el = libraryItemRefs.current.get(active);
      const container = sidebarScrollRef.current;
      if (!el || !container) return;
      const cRect = container.getBoundingClientRect();
      const eRect = el.getBoundingClientRect();
      container.scrollTo({
        top:
          container.scrollTop +
          (eRect.top - cRect.top) -
          container.clientHeight / 2 +
          el.offsetHeight / 2,
        behavior: "smooth",
      });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [location.pathname, listLibrary, modalOnly]);

  useEffect(() => {
    if (modalOnly || !openedLibrary) return;
    let timer: number | undefined;
    const frame = window.requestAnimationFrame(() => {
      timer = window.setTimeout(() => {
        const container = sidebarScrollRef.current;
        const el = subMenuRefs.current.get(openedLibrary);
        if (!container || !el) return;
        const cRect = container.getBoundingClientRect();
        const eRect = el.getBoundingClientRect();
        const bottom = eRect.bottom - cRect.bottom;
        const top = eRect.top - cRect.top;
        if (bottom > 0)
          container.scrollTo({
            top: container.scrollTop + bottom + 16,
            behavior: "smooth",
          });
        else if (top < 0)
          container.scrollTo({
            top: container.scrollTop + top - 16,
            behavior: "smooth",
          });
      }, 120);
    });
    return () => {
      window.cancelAnimationFrame(frame);
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [openedLibrary, listLibrary, modalOnly]);

  /* ── 워크플로우 헬퍼: 이름 표준화, 라우트 생성, 아이콘 매핑 ── */
  // 백엔드/기존 데이터에서 들어오는 workflow 이름을 화면 표준 이름으로 통일
  const normalizeWorkflowName = (name: string): string => {
    if (!name) return "";
    const l = name.toLowerCase().trim();
    if (l.includes("ppi")) return "PPI Inhibitor";
    if (l.includes("kinase")) return "Kinase Inhibitor";
    if (l.includes("mdtr")) return "MDTR";
    if (l.includes("toxicity") || l.includes("chemtox")) return "Drug Toxicity";
    if (l.includes("biomarker") || l.includes("chemtarget")) return "Biomarker";
    if (l.includes("drug") || l.includes("response")) return "Drug Response";
    return name;
  };

  // 워크플로우 이름과 타입을 실제 라우터 path 세그먼트로 변환
  const getWorkflowPath = (wfName: string, toolType?: string) => {
    const n = normalizeWorkflowName(wfName);
    if (n === "Drug Response")
      return toolType?.toLowerCase() === "clinical"
        ? "drug-response/clinical"
        : "drug-response/preclinical";
    if (n === "Drug Toxicity") {
      const normalizedToolType = toolType?.toLowerCase();
      if (normalizedToolType === "mdtr") return "drug-toxicity/mdtr";
      if (normalizedToolType === "animal") return "drug-toxicity/animal";
      return "drug-toxicity/fatetox";
    }
    if (n === "MDTR") return "drug-toxicity/mdtr";
    if (n === "Biomarker") return "biomarker";
    return (
      {
        "PPI Inhibitor": "ppi-inhibitor",
        "Kinase Inhibitor": "kinase-inhibitor",
      }[n] ?? "ppi-inhibitor"
    );
  };
  const isDrugResponseWorkflow = (n?: string) =>
    normalizeWorkflowName(n || "") === "Drug Response";
  const isDrugToxicityWorkflow = (n?: string) =>
    normalizeWorkflowName(n || "") === "Drug Toxicity";
  const isMdtrWorkflow = (n?: string) =>
    normalizeWorkflowName(n || "") === "MDTR";
  const isBiomarkerWorkflow = (n?: string) =>
    normalizeWorkflowName(n || "") === "Biomarker";
  const isDrugResponsePath = (p?: string) =>
    typeof p === "string" && p.startsWith("drug-response/");
  const isDrugToxicityPath = (p?: string) =>
    typeof p === "string" && p.startsWith("drug-toxicity");
  const isMdtrPath = (p?: string) =>
    typeof p === "string" && p.startsWith("mdtr");
  const isBiomarkerPath = (p?: string) =>
    typeof p === "string" && p.startsWith("biomarker");

  // 워크스페이스 클릭/생성 후 이동할 최종 경로를 조립
  const buildLibraryTargetPath = ({
    workflowName,
    workflowType,
    libraryName,
    roundNum = 1,
  }: any) => {
    const wp = getWorkflowPath(workflowName, workflowType);
    if (isDrugResponsePath(wp) || isDrugToxicityPath(wp) || isMdtrPath(wp) || isBiomarkerPath(wp))
      return `/${wp}/${libraryName}/round1`;
    return `/${wp}/${libraryName}/round${roundNum}`;
  };
  const getWorkflowFromPath = (pathname: string) => {
    if (pathname.includes("/ppi-inhibitor/")) return "PPI Inhibitor";
    if (pathname.includes("/kinase-inhibitor/")) return "Kinase Inhibitor";
    if (pathname.includes("/mdtr/")) return "MDTR";
    if (pathname.includes("/drug-toxicity/")) return "Drug Toxicity";
    if (pathname.includes("/drug-response/")) return "Drug Response";
    if (pathname.includes("/biomarker/")) return "Biomarker";
    return "PPI Inhibitor";
  };
  const getWorkflowIcon = (wfName: string) => {
    const cfg = WORKFLOW_CONFIG[normalizeWorkflowName(wfName)];
    if (!cfg)
      return <SummarizeIcon sx={{ fontSize: 18, color: TOKENS.textMuted }} />;
    return (
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: "7px",
          background: cfg.bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <cfg.Icon sx={{ fontSize: 14, color: cfg.color }} />
      </Box>
    );
  };

  /* ── 툴 메뉴 active 판정: workspace 경로에서는 툴 자체를 active 처리하지 않음 ── */
  const isWorkspacePath = (base: string) => {
    // /ppi-inhibitor/{libraryName}/... 처럼 base 뒤에 libraryName 세그먼트가 있으면 workspace 경로
    const parts = location.pathname.split("/").filter(Boolean);
    return parts[0] === base && parts.length >= 2;
  };
  const isPPIToolActive = () =>
    location.pathname === "/ppi-inhibitor" ||
    (location.pathname.startsWith("/ppi-inhibitor") &&
      !isWorkspacePath("ppi-inhibitor"));
  const isKinaseToolActive = () =>
    location.pathname === "/kinase-inhibitor" ||
    (location.pathname.startsWith("/kinase-inhibitor") &&
      !isWorkspacePath("kinase-inhibitor"));
  const isDrugResponseToolActive = () => {
    if (!location.pathname.startsWith("/drug-response")) return false;
    const parts = location.pathname.split("/").filter(Boolean);
    // /drug-response, /drug-response/clinical, /drug-response/preclinical 이면서 workspace(3번째 세그먼트) 없으면 tool active
    if (parts.length <= 2) return true;
    // parts[1] 이 clinical/preclinical 이고 parts[2] 가 있으면 workspace 경로
    if ((parts[1] === "clinical" || parts[1] === "preclinical") && parts[2])
      return false;
    return false;
  };
  const isDrugToxicityToolActive = () =>
    location.pathname === "/drug-toxicity" ||
    (location.pathname.startsWith("/drug-toxicity") &&
      !isWorkspacePath("drug-toxicity"));

  const isMdtrToolActive = () =>
    location.pathname === '/mdtr' ||
    (location.pathname.startsWith('/mdtr') &&
       !isWorkspacePath('mdtr'));

  const isBiomarkerToolActive = () =>
    location.pathname === "/biomarker" ||
    (location.pathname.startsWith("/biomarker") && !isWorkspacePath("biomarker"));

  /* ── 워크스페이스 라운드 헬퍼 ── */
  const getLibraryRounds = (name: string) => {
    const info = listLibrary?.find((l: any) => l.library_name === name);
    return Array.isArray(info?.created_round) ? info.created_round : [];
  };
  const hasRoundItems = (name: string) => getLibraryRounds(name).length > 0;
  const getMaxRound = (name: string) => {
    const rounds = getLibraryRounds(name);
    return rounds.length === 0
      ? 0
      : Math.max(...rounds.map((r: any) => parseInt(r)));
  };

  /* ── 공통 이벤트. 모달, 사이드바 토글, 툴 진입을 처리. ── */
  // 사용자 메뉴. 아바타 클릭 위치를 기준으로 AvatarMenu를 엶.
  const handleAvatarMenuOpen = (e: any) =>
    dispatch(openAvatarMenu({ anchorEl: e.currentTarget }));

  // 확인 Dialog 열기. 삭제/이름변경 대상 워크스페이스와 Dialog 타입을 저장.
  const handleDialogOpen = (id: any, type = "chat") => {
    setOpen(true);
    setItemName(id);
    setDialogType(type);
    if (type === "editLibrary") {
      setEditedLibraryName(id);
      setRenameError("");
    }
  };

  // 확인 Dialog 닫기. 선택값과 이름변경 입력 상태를 초기화.
  const handleDialogClose = () => {
    setOpen(false);
    setItemName(null);
    setEditedLibraryName("");
    setRenameError("");
  };

  // 크레딧 메뉴. 결제 이력 이동과 잔여 크레딧 재조회 액션.
  const handleMovePaymentHistoryPage = () => navigate("/payment-history");
  const handleRefreshCreditInfo = () => dispatch(getUserMyCreditBalanceAPI());

  // 다음 라운드. 선택한 워크스페이스의 최대 라운드 다음 번호를 계산해 모달을 엶.
  const handleNextRoundClick = (name: string, e: any) => {
    e.stopPropagation();
    setSelectedLibraryForNextRound(name);
    setNextRoundNumber(getMaxRound(name) + 1);
    setShowNextRoundModal(true);
  };

  // 삭제 버튼. 리스트 row 클릭과 충돌하지 않도록 이벤트를 막고 삭제 Dialog를 엶.
  const handleDeleteIconClick = (name: string, e: any) => {
    e.stopPropagation();
    e.preventDefault();
    handleDialogOpen(name, "library");
  };

  // 채팅 삭제. Drawer 내부 itemList에서 채팅 항목을 제거.
  const deleteChat = (id: any) => {
    if (typeof id === "string") dispatch(deleteDrawerItem(id));
    setItemName(null);
  };

  // 사이드바 접기. react-pro-sidebar와 Redux drawer width를 함께 동기화.
  const toggleSidebar = () => {
    const next = !isCollapsed;
    setIsCollapsed(next);
    collapseSidebar(next);
    dispatch(setDrawerWidth(next ? collapsedWidth : expandedWidth));
    dispatch(next ? closeDrawer() : openDrawer());
    document.body.style.setProperty(
      "--drawer-width",
      `${next ? collapsedWidth : expandedWidth}px`
    );
  };

  // 활성 메뉴 판정. 현재 pathname 또는 query id와 메뉴 path를 비교.
  const isItemActive = (path: string) =>
    location.pathname === path || pageId === path.split("/")?.pop();

  // PPI 메뉴 선택. assay 선택 모달을 거쳐 워크스페이스 생성으로 진입.
  const handlePPIMenuClick = (e: any) => {
    e.preventDefault();
    if (isCreatingLibrary) return;
    dispatch(setUserChoiceTool("PPI Inhibitor"));
    setShowAssayChoiceModal(true);
  };

  // Kinase 메뉴 선택. PPI와 동일하게 assay 선택 후 생성 모달로 진입.
  const handleKinaseMenuClick = (e: any) => {
    e.preventDefault();
    if (isCreatingLibrary) return;
    dispatch(setUserChoiceTool("Kinase Inhibitor"));
    setShowAssayChoiceModal(true);
  };

  // Drug Response 메뉴 선택. Clinical/Preclinical 선택 모달을 먼저 표시.
  const handleDrugResponseMenuClick = (e: any) => {
    e.preventDefault();
    if (isCreatingLibrary) return;
    setOpenedLibrary(null);
    dispatch(setUserChoiceTool("Drug Response"));
    dispatch(setUserHaveAssayFile(false));
    setShowDrugResponseModal(true);
    setModalKey((p) => p + 1);
  };

  const handleMdtrMenuClick = (e:any) => {
    e.preventDefault();
    if (isCreatingLibrary) return;
    setOpenedLibrary(null);
    dispatch(setUserChoiceTool("Drug Toxicity"));
    dispatch(setUserChoiceToolType("mdtr"));
    dispatch(setUserHaveAssayFile(false));
    setShowMdtrModal(false);
    setModalKey((p)=> p + 1);
    setShowPPIModal(true);
  }

  // Drug Toxicity 메뉴 선택. assay 없이 바로 워크스페이스 생성 모달로 진입.
  const handleDrugToxicityMenuClick = (e: any) => {
    e.preventDefault();
    if (isCreatingLibrary) return;
    setOpenedLibrary(null);
    dispatch(setUserChoiceTool("Drug Toxicity"));
    dispatch(setUserHaveAssayFile(false));
    setShowDrugToxicityModal(true);
    setModalKey((p) => p + 1);
  };

  const handleBiomarkerMenuClick = (e: any) => {
    e.preventDefault();
    if (isCreatingLibrary) return;
    setOpenedLibrary(null);
    dispatch(setUserChoiceTool("Biomarker"));
    dispatch(setUserHaveAssayFile(false));
    setModalKey((p) => p + 1);
    setShowPPIModal(true);
  };

  // Assay 없음. assay 업로드 없이 워크스페이스 생성 모달을 엶.
  const handleNoAssayFile = () => {
    setShowAssayChoiceModal(false);
    setShowDrugResponseModal(false);
    setShowDrugToxicityModal(false);
    setShowMdtrModal(false)
    setModalKey((p) => p + 1);
    dispatch(setUserHaveAssayFile(false));
    setShowPPIModal(true);
  };

  // Assay 있음. assay 사용 상태를 저장하고 워크스페이스 생성 모달을 엶.
  const handleWithAssayFile = () => {
    setShowAssayChoiceModal(false);
    setModalKey((p) => p + 1);
    dispatch(setUserHaveAssayFile(true));
    setShowPPIModal(true);
  };

  // Assay 선택 닫기. 외부 콜백에 사용자가 닫았음을 전달.
  const handleAssayChoiceModalClose = () => {
    setShowAssayChoiceModal(false);
    if (onNextStep) onNextStep({ closed: true, success: false });
  };

  // Drug Response 선택 닫기. Clinical/Preclinical 선택 취소를 상위에 전달.
  const handleDrugResponseModalClose = () => {
    setShowDrugResponseModal(false);
    if (onNextStep) onNextStep({ closed: true, success: false });
  };

  const handleDrugToxicityModalClose = () => {
    setShowDrugToxicityModal(false);
    if (onNextStep) onNextStep({ closed: true, success: false });
  };

  const handleMdtrModalClose = () => {
    setShowMdtrModal(false);
    if (onNextStep) onNextStep({ closed: true , success : false})
  }

  // 워크스페이스 생성 성공. 목록을 갱신하고 새 워크스페이스 round1 경로로 이동.
  const handleLibraryCreated = async (lib: any) => {
    if (lib?.name) {
      try {
        setIsCreatingLibrary(true);
        dispatch(setLibrary(lib.name));
        const sel = lib.selectedTool || userChoiceSelectTool;
        await dispatch(listLibraryAPI({ group_name: "temp_group" })).unwrap();
        setOpenedLibrary(lib.name);
        const tp = buildLibraryTargetPath({
          workflowName: sel,
          workflowType:
            lib.workflow_type || selectedDrugToxicityType || userChoiceDrugResponseSelectToolType,
          libraryName: lib.name,
          roundNum: 1,
        });
        setTimeout(() => {
          navigate(tp, { replace: true });
          setIsCreatingLibrary(false);
          if (onNextStep)
            onNextStep({
              success: true,
              name: lib.name,
              workflow_type: lib.workflow_type || selectedDrugToxicityType || userChoiceDrugResponseSelectToolType,
              selectedTool: sel,
              library: lib,
              closed: true,
            });
        }, 500);
      } catch (err) {
        console.error(err);
        setIsCreatingLibrary(false);
      }
    }
    setShowPPIModal(false);
  };

  // 생성 모달 닫기. 성공이면 생성 후 이동, 취소면 생성 상태만 초기화.
  const handleModalClose = (data: any) => {
    setShowPPIModal(false);
    if (data?.success) handleLibraryCreated(data);
    else {
      setIsCreatingLibrary(false);
      if (onNextStep) onNextStep({ closed: true, success: false });
    }
  };

  // 워크스페이스 선택. 워크플로우 타입/라운드에 맞는 상세 경로로 이동.
  const handleLibraryClick = async (
    name: string,
    wfName?: string,
    wfType?: string,
    round = 1
  ) => {
    if (isDrugResponseWorkflow(wfName) || isDrugToxicityWorkflow(wfName) ||
        isMdtrWorkflow(wfName) || isBiomarkerWorkflow(wfName)) setOpenedLibrary(null);
    dispatch(setLibrary(name));
    if (wfName)
      navigate(
        buildLibraryTargetPath({
          workflowName: wfName,
          workflowType: wfType,
          libraryName: name,
          roundNum: round,
        }),
        { replace: true }
      );
  };

  // 텍스트 표시. 긴 워크스페이스명을 Drawer 폭에 맞게 축약.
  const truncateText = (text: string, max = 12) =>
    text?.length <= max ? text : text.slice(0, max) + "…";

  // 워크스페이스명 정리. 백엔드에서 허용하는 문자만 남김.
  const sanitizeWorkspaceName = (value: string) =>
    value.replace(/[^a-zA-Z0-9가-힣_.:=+\-@]/g, "");

  // 실행 여부 확인. wholeWorkflow 응답에서 해당 워크스페이스가 RUNNING인지 검사.
  const isWorkspaceRunningInWholeWorkflow = (
    workspaceName: string,
    wholeWorkflowResponse: any
  ) => {
    const lists = wholeWorkflowResponse?.round_library_lists;
    if (!lists) return false;

    return Object.values(lists).some((workflowRounds: any) =>
      Object.values(workflowRounds || {}).some((roundStatuses: any) =>
        Array.isArray(roundStatuses?.RUNNING) &&
        roundStatuses.RUNNING.includes(workspaceName)
      )
    );
  };

  // 실행 상태 판정. 중첩 객체까지 재귀적으로 확인해 running 계열 상태를 찾음.
  const isRunningWorkflowStatus = (status: any): boolean => {
    if (!status) return false;
    if (typeof status === "object")
      return Object.values(status).some((value) => isRunningWorkflowStatus(value));

    const normalized = String(status).trim().toUpperCase();
    return (
      normalized.includes("RUNNING") ||
      normalized.includes("SUBMITTED") ||
      normalized.includes("WAITING") ||
      normalized.includes("READY TO RUN") ||
      normalized.includes("STARTING UP") ||
      normalized.includes("INITIALIZING")
    );
  };

  // 상태 API 확인. 각 라운드별 workflow/status를 조회해 실제 실행 중 여부를 보강 검사.
  const isWorkspaceRunningInStatusAPI = async (workspaceName: string) => {
    const info = listLibrary?.find((lib: any) => lib.library_name === workspaceName);
    const workflowName = normalizeWorkflowName(
      info?.workflow_name || getWorkflowFromPath(location.pathname)
    );
    const rounds = getLibraryRounds(workspaceName);
    const targetRounds = rounds.length > 0 ? rounds : [1];
    const djangoEndpoint = getDjangoEndpoint();

    for (const round of targetRounds) {
      try {
        const response = await axiosInstance.post(`${djangoEndpoint}/workflow/status/`, {
          workflow_name: workflowName,
          group_name: "temp_group",
          library_name: workspaceName,
          round: parseInt(String(round), 10) || 1,
        });
        const data = response?.data;
        if (
          isRunningWorkflowStatus(data?.batch_status ?? data?.batchStatus) ||
          isRunningWorkflowStatus(data?.overall_status ?? data?.overallStatus)
        )
          return true;
      } catch (err) {
        console.error(err);
      }
    }

    return false;
  };

  // 워크스페이스 이름 변경. 중복/실행 중 여부를 확인한 뒤 서버와 현재 URL을 갱신.
  const handleRenameLibrary = async (old: string, nw: string) => {
    const sanitizedNewName = sanitizeWorkspaceName(nw).trim();
    if (typeof old === "string" && sanitizedNewName !== "") {
      const isDuplicateName = listLibrary?.some(
        (lib: any) =>
          lib.library_name === sanitizedNewName && lib.library_name !== old
      );

      if (isDuplicateName) {
        setRenameError(t("PPI.modal.rename_duplicate_error"));
        return;
      }

      setRenameError("");
      try {
        let wholeWorkflowResponse = dashboardWholeWorkflow;
        try {
          wholeWorkflowResponse = await dispatch(wholeWorkflowAPI()).unwrap();
        } catch (err) {
          console.error(err);
        }

        const isRunning =
          isWorkspaceRunningInWholeWorkflow(old, wholeWorkflowResponse) ||
          (await isWorkspaceRunningInStatusAPI(old));

        if (isRunning) {
          setRunningCheckAction("rename");
          setModalType("running-checking");
          setRunningModal(true);
          return;
        }

        await dispatch(
          renameLibraryAPI({
            group_name: "temp_group",
            current_name: old,
            new_name: sanitizedNewName,
          })
        ).unwrap();
        await dispatch(listLibraryAPI({ group_name: "temp_group" })).unwrap();
        const wp = getWorkflowPath(getWorkflowFromPath(location.pathname));
        if (location.pathname.includes(`/${wp}/${old}`))
          navigate(
            location.pathname.replace(`/${wp}/${old}`, `/${wp}/${sanitizedNewName}`),
            {
              replace: true,
            }
          );
        handleDialogClose();
      } catch (err) {
        console.error(err);
        const errorPayload = err as any;
        const serverMessage =
          typeof errorPayload === "string"
            ? errorPayload
            : errorPayload?.detail ||
              errorPayload?.message ||
              errorPayload?.error;
        setRenameError(
          typeof serverMessage === "string" && serverMessage.trim()
            ? serverMessage
            : t("PPI.modal.rename_failed_error")
        );
      }
    }
  };

  // 워크스페이스 삭제. 실행 중이면 확인 모달을 띄우고, 삭제 후 대체 경로로 이동.
  const handleDeleteLibrary = async (name: string) => {
    if (typeof name === "string") {
      try {
        let wholeWorkflowResponse = dashboardWholeWorkflow;
        try {
          wholeWorkflowResponse = await dispatch(wholeWorkflowAPI()).unwrap();
        } catch (err) {
          console.error(err);
        }

        if (isWorkspaceRunningInWholeWorkflow(name, wholeWorkflowResponse)) {
          setRunningCheckAction("delete");
          setModalType("running-checking");
          setRunningModal(true);
          return;
        }

        await dispatch(
          deleteLibraryAPI({ group_name: "temp_group", library_name: name })
        ).unwrap();
        dispatch(removeLibraryFromList(name));
        const updated = (listLibrary || []).filter(
          (lib: any) => lib.library_name !== name
        );
        dispatch(listLibraryAPI({ group_name: "temp_group" }))
          .unwrap()
          .catch((err) => console.error(err));
        if (location.pathname.includes(`/${name}`)) {
          const sorted = [...updated].sort((a: any, b: any) =>
            (b.created_at || "").localeCompare(a.created_at || "")
          );
          if (sorted.length > 0)
            navigate(
              buildLibraryTargetPath({
                workflowName: sorted[0].workflow_name,
                workflowType: sorted[0].workflow_type,
                libraryName: sorted[0].library_name,
                roundNum: 1,
              }),
              { replace: true }
            );
          else navigate("/dashboard", { state: { fromDelete: true } });
        }
        handleDialogClose();
      } catch (err) {
        console.error(err);
        handleDialogClose();
      }
    }
  };

  // 강제 삭제. 실행 중 안내를 확인한 뒤 사용자가 삭제를 강행한 경우 처리.
  const handleForceDelete = async () => {
    setRunningModal(false);
    if (typeof itemName === "string") {
      try {
        await dispatch(
          deleteLibraryAPI({ group_name: "temp_group", library_name: itemName })
        ).unwrap();
        dispatch(removeLibraryFromList(itemName));
        const updated = (listLibrary || []).filter(
          (lib: any) => lib.library_name !== itemName
        );
        dispatch(listLibraryAPI({ group_name: "temp_group" }))
          .unwrap()
          .catch((err) => console.error(err));
        if (location.pathname.includes(`/${itemName}`)) {
          const sorted = [...updated].sort((a: any, b: any) =>
            (b.created_at || "").localeCompare(a.created_at || "")
          );
          if (sorted.length > 0)
            navigate(
              buildLibraryTargetPath({
                workflowName: sorted[0].workflow_name,
                workflowType: sorted[0].workflow_type,
                libraryName: sorted[0].library_name,
                roundNum: 1,
              }),
              { replace: true }
            );
          else navigate("/dashboard", { replace: true });
        }
        handleDialogClose();
      } catch (err) {
        console.error(err);
        handleDialogClose();
      }
    }
  };

  // 강제 삭제 취소. 실행 중 확인 모달을 닫고 기존 Dialog 상태를 정리.
  const handleCancelForceDelete = () => {
    setRunningModal(false);
    setRunningCheckAction("delete");
    handleDialogClose();
  };

  // Dialog Enter 처리. 이름변경/삭제 확인 Dialog에서 Enter 키로 기본 액션을 실행.
  const handleWorkspaceDialogEnter = (
    event: React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing)
      return;

    if (dialogType === "editLibrary") {
      const canRename =
        !renameLoading &&
        editedLibraryName.trim() !== "" &&
        editedLibraryName !== itemName;
      if (canRename) {
        event.preventDefault();
        handleRenameLibrary(itemName, editedLibraryName);
      }
      return;
    }

    if (dialogType === "library" && !deleteLoading) {
      event.preventDefault();
      handleDeleteLibrary(itemName);
    }
  };

  // 외부 모달 트리거. Dashboard 등 외부에서 showAssayModal로 들어온 생성을 처리.
  useEffect(() => {
    if (!showAssayModal) return;
    if (selectedTool === "Drug Response") {
      dispatch(setUserChoiceTool("Drug Response"));
      dispatch(setUserHaveAssayFile(false));
      if (selectedDrugResponseMode === "clinical") {
        dispatch(setUserDrugResponseChoiceTool("Clinical"));
        dispatch(setUserChoiceToolType("clinical"));
        handleNoAssayFile();
      } else if (selectedDrugResponseMode === "preclinical") {
        dispatch(setUserDrugResponseChoiceTool("Preclinical"));
        dispatch(setUserChoiceToolType("preclinical"));
        handleNoAssayFile();
      } else {
        setModalKey((p) => p + 1);
        setShowDrugResponseModal(true);
      }
    } else if (selectedTool === "Kinase Inhibitor") {
      dispatch(setUserChoiceTool("Kinase Inhibitor"));
      setShowAssayChoiceModal(true);
    } else if (selectedTool === "PPI Inhibitor") {
      dispatch(setUserChoiceTool("PPI Inhibitor"));
      setShowAssayChoiceModal(true);
    } else if (selectedTool === "Drug Toxicity") {
      dispatch(setUserChoiceTool("Drug Toxicity"));
      dispatch(setUserHaveAssayFile(false));
      setShowDrugToxicityModal(true);
      setModalKey((p) => p + 1);
    } else if (selectedTool === "FateTox") {
      dispatch(setUserChoiceTool("Drug Toxicity"));
      dispatch(setUserChoiceToolType("fatetox"));
      setSelectedDrugToxicityType("fatetox");
      dispatch(setUserHaveAssayFile(false));
      setModalKey((p) => p + 1);
      setShowPPIModal(true);
    } else if (selectedTool === "MDTR") {
      dispatch(setUserChoiceTool("Drug Toxicity"));
      dispatch(setUserChoiceToolType("mdtr"));
      setSelectedDrugToxicityType("mdtr");
      dispatch(setUserHaveAssayFile(false));
      setModalKey((p => p + 1));
      setShowPPIModal(true);
    } else if (selectedTool === "Animal") {
      dispatch(setUserChoiceTool("Drug Toxicity"));
      dispatch(setUserChoiceToolType("animal"));
      setSelectedDrugToxicityType("animal");
      dispatch(setUserHaveAssayFile(false));
      setModalKey((p) => p + 1);
      setShowPPIModal(true);
    } else if (selectedTool === "Biomarker") {
      dispatch(setUserChoiceTool("Biomarker"));
      dispatch(setUserHaveAssayFile(false));
      setModalKey((p) => p + 1);
      setShowPPIModal(true);
    }
  }, [showAssayModal, selectedTool, selectedDrugResponseMode, dispatch]);

  /* ── pro-sidebar styles ── */
  const menuItemStyles = {
    button: {
      height: "38px",
      minHeight: "38px",
      boxSizing: "border-box" as const,
      alignItems: "center",
      borderRadius: "9px",
      margin: "2px 10px",
      padding: "0 12px",
      transition:
        "background-color 0.16s ease, color 0.16s ease, box-shadow 0.16s ease",
      border: "1px solid transparent",
      "&:hover": {
        backgroundColor: "rgba(255,255,255,0.86)",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
        color: TOKENS.text,
      },
      "&.ps-active": {
        backgroundColor: TOKENS.accent,
        color: "#fff",
        fontWeight: 700,
        boxShadow: "0 8px 18px rgba(37,79,135,0.18)",
      },
    },
    root: { fontSize: "13.5px", fontWeight: 600 },
    icon: { minWidth: "34px", color: TOKENS.textMuted },
    label: {},
    subMenuContent: {
      paddingLeft: "6px",
      backgroundColor: "transparent",
    },
  };

  const SectionLabel = ({ label }: { label: string }) =>
    !isCollapsed ? (
      <Typography
        sx={{
          fontSize: "10.5px",
          fontWeight: 700,
          color: "#8a96a6",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          px: 2.75,
          pt: 1.6,
          pb: 0.6,
        }}
      >
        {label}
      </Typography>
    ) : null;

  /* ─────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────── */
  // Drawer 렌더링. 고정 사이드바, 툴 메뉴, 워크스페이스 목록, 하단 계정을 표시.
  return (
    <>
      <style>{MODAL_ANIMATIONS}</style>

      {/* 사이드바 프레임. modalOnly 인스턴스는 무거운 Sidebar 트리를 생성하지 않음. */}
      {!modalOnly && (
      <Box
        onMouseOver={preloadLinkedRoute}
        onFocusCapture={preloadLinkedRoute}
        sx={{
          position: "fixed",
          left: 0,
          top: 0,
          height: "100vh",
          zIndex: 1100,
          display: "flex",
          width: isCollapsed ? `${collapsedWidth}px` : `${drawerWidth}px`,
          transition: "width 0.25s cubic-bezier(0.4,0,0.2,1)",
          overflow: "hidden",
        }}
      >
        <Sidebar
          width={isCollapsed ? `${collapsedWidth}px` : `${drawerWidth}px`}
          // react-pro-sidebar 는 collapsed 상태에서 width 대신 자체 collapsedWidth(기본 '80px')를
          // 사용한다. 명시하지 않으면 실제 폭 80px 과 바깥 Box 폭 64px 이 어긋나
          // overflow:hidden 에 의해 사이드바 오른쪽이 16px 잘린다.
          collapsedWidth={`${collapsedWidth}px`}
          backgroundColor={TOKENS.bg}
          rootStyles={{
            color: TOKENS.text,
            height: "100vh",
            borderRight: `1px solid ${TOKENS.border}`,
            boxShadow: "6px 0 24px rgba(15,23,42,0.05)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            "& .ps-sidebar-container": {
              backgroundColor: TOKENS.bg,
            },
            "& .ps-menu-button": {
              minHeight: "38px !important",
              height: "38px !important",
              boxSizing: "border-box",
              alignItems: "center",
            },
            "& .ps-menu-button.ps-active svg": {
              color: "#fff",
            },
            "& .ps-menu-icon": {
              marginRight: "2px",
            },
            "& .ps-menu-label": {
              display: "block",
              lineHeight: "20px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
          }}
          collapsed={isCollapsed}
        >
          {/* ── HEADER ── */}
          <Box
            sx={{
              px: isCollapsed ? 1 : 2,
              py: 1.25,
              minHeight: 68,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
position: "relative",
              backgroundColor: TOKENS.bgSubtle,
              borderBottom: `1px solid ${TOKENS.border}`,
            }}
          >
            {!isCollapsed && (
              <Box
                onClick={() => navigate("/dashboard")}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  minWidth: 0,
                }}
              >
                <img
                  src={AppLogoLightMode}
                  alt="logo"
                  height={40}
                  style={{ verticalAlign: "middle" }}
                />
              </Box>
            )}
<IconButton
  onClick={toggleSidebar}
  size="medium"
  sx={{
    position: "absolute",   // 추가
    right: 18,              // 추가
    width: 36,
    height: 36,
    borderRadius: "8px",
    color: TOKENS.textSub,
    backgroundColor: TOKENS.bg,
    border: `1px solid ${TOKENS.border}`,
    "&:hover": {
      backgroundColor: TOKENS.bgMuted,
      color: TOKENS.accent,
    },
  }}
>
              <MenuIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </Box>

          {/* ── USER / CREDIT ── */}
          <Box
            sx={{
              borderBottom: `1px solid ${TOKENS.border}`,
              p: isCollapsed ? 0 : 1.25,
              backgroundColor: TOKENS.bgSubtle,
            }}
          >
            {/* 유저 영역 */}
            <Box
              sx={{
                px: isCollapsed ? 1 : 1.25,
                py: isCollapsed ? 1.25 : 1.15,
                position: "relative",
                display: "flex",
                flexDirection: isCollapsed ? "column" : "row",
                alignItems: "center",
                justifyContent: isCollapsed ? "center" : "flex-start",
                gap: 1,
                borderRadius: isCollapsed ? 0 : "10px",
                backgroundColor: isCollapsed ? "transparent" : "#f8fafc",
                border: isCollapsed ? "none" : `1px solid ${TOKENS.border}`,
              }}
            >
              {!isCollapsed && (
                <IconButton
                  onClick={handleAvatarMenuOpen}
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 9,
                    right: 9,
                    width: 27,
                    height: 27,
                    borderRadius: "7px",
                    color: TOKENS.textMuted,
                    "&:hover": {
                      background: TOKENS.bgMuted,
                      color: TOKENS.accent,
                    },
                  }}
                >
                  <SettingsIcon sx={{ fontSize: 20 }} />
                </IconButton>
              )}

              <Tooltip title={isCollapsed ? email : ""} placement="right">
                <Avatar
                  onClick={isCollapsed ? handleAvatarMenuOpen : undefined}
                  sx={{
                    width: isCollapsed ? 32 : 34,
                    height: isCollapsed ? 32 : 34,
                    flexShrink: 0,
                    backgroundColor: getAvatarColor(userInitial),
                    fontSize: isCollapsed ? "1rem" : "0.98rem",
                    fontWeight: 800,
                    cursor: isCollapsed ? "pointer" : "default",
                    boxShadow: "0 0 0 2px #fff",
                  }}
                >
                  {userInitial}
                </Avatar>
              </Tooltip>

              {!isCollapsed && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: 0.25,
                    minWidth: 0,
                    pr: 3,
                  }}
                >
                  <Typography
                    noWrap
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.84rem",
                      color: TOKENS.text,
                      lineHeight: 1.2,
                      maxWidth: "100%",
                    }}
                  >
                    {email}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.72rem",
                      color: TOKENS.textMuted,
                      fontWeight: 500,
                    }}
                  >
                    {t("drawer.member")}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* credit */}
            {!isCollapsed && (
              <Box
                sx={{
                  mt: 1,
                  borderRadius: "10px",
                  background: "#f8fafc",
                  border: `1px solid ${TOKENS.border}`,
                  p: 1,
                  boxShadow: "none",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 0.75,
                  }}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "8px",
                        background: "#eef5ff",
                        border: `1px solid ${TOKENS.border}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <DiamondOutlinedIcon
                        sx={{ fontSize: 15, color: TOKENS.accent }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "0.72rem",
                        fontWeight: 800,
                        color: TOKENS.textSub,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {t("credit.balance")}
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={handleRefreshCreditInfo}
                    size="small"
                    sx={{
                      width: 25,
                      height: 25,
                      borderRadius: "6px",
                      color: TOKENS.textMuted,
                      "&:hover": {
                        background: TOKENS.bg,
                        color: TOKENS.accent,
                      },
                    }}
                  >
                    <RefreshIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    px: 0.5,
                    py: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: "1.05rem",
                      color: TOKENS.text,
                      lineHeight: 1,
                    }}
                  >
                    {myCredit != null ? myCredit.toLocaleString() : "0"}
                  </Typography>
                </Box>
                <Button
                  onMouseEnter={() => preloadRouteForPath("/payment-history")}
                  onFocus={() => preloadRouteForPath("/payment-history")}
                  onClick={handleMovePaymentHistoryPage}
                  fullWidth
                  size="small"
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: TOKENS.accent,
                    textTransform: "none",
                    py: 0.55,
                    borderRadius: "7px",
                    border: "none",
                    background: "transparent",
                    justifyContent: "center",
                    "&:hover": {
                      background: "rgba(49,95,159,0.06)",
                      borderColor: "rgba(49,95,159,0.2)",
                    },
                  }}
                  endIcon={
                    <ChevronRightIcon sx={{ fontSize: "15px !important" }} />
                  }
                >
                  {t("credit.historyView")}
                </Button>
              </Box>
            )}

            {/* collapsed credit */}
            {isCollapsed && (
              <Tooltip title={t("drawer.credits", { amount: myCredit ?? 0 })} placement="right">
                <Box
                  onClick={handleRefreshCreditInfo}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    py: 1,
                    cursor: "pointer",
                  }}
                >
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: "8px",
                      background: TOKENS.bgSubtle,
                      border: `1px solid ${TOKENS.border}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <DiamondOutlinedIcon
                      sx={{ fontSize: 17, color: TOKENS.accent }}
                    />
                  </Box>
                </Box>
              </Tooltip>
            )}
          </Box>

          {/* ── RESIZE HANDLE ── */}
          {!isCollapsed && (
            <Box
              onMouseDown={handleResizeStart}
              sx={{
                position: "absolute",
                right: 0,
                top: 0,
                bottom: 0,
                width: "4px",
                cursor: "col-resize",
                zIndex: 1200,
                backgroundColor: isResizing ? TOKENS.accent : "transparent",
                transition: "background-color 0.2s",
                "&:hover": { backgroundColor: TOKENS.accent, opacity: 0.5 },
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: "-3px",
                  right: "-3px",
                  top: 0,
                  bottom: 0,
                },
              }}
            />
          )}

          {/* ── MENU AREA ── */}
          <Box
            ref={sidebarScrollRef}
            sx={{
              flex: 1,
              pt: 0.5,
              overflowY: "auto",
              overflowX: "hidden",
              "&::-webkit-scrollbar": { width: "3px" },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                background: TOKENS.border,
                borderRadius: "99px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: TOKENS.textMuted,
              },
              scrollbarWidth: "thin",
              scrollbarColor: `${TOKENS.border} transparent`,
            }}
          >
            <Menu menuItemStyles={menuItemStyles} style={{ paddingTop: 6 }}>
            {isAdmin && (
              <>
              <SectionLabel label={t("drawer.admin")} />
              </>
            )}
              {/* Admin */}
              {isAdmin && (

                <SubMenu
                  icon={
                    <AdminPanelSettingsTwoToneIcon
                      sx={{ fontSize: 20, color: TOKENS.textMuted }}
                    />
                  }
                  label={
                    !isCollapsed ? (
                      <Typography sx={{fontSize: "14px" , fontWeight:700 }}>
                      {t("drawer.admin")}
                    </Typography>
                    ) : (
                      <Tooltip title={t("drawer.admin")} placement="right">
                        <span />
                      </Tooltip>
                    )
                  }
                  defaultOpen={location.pathname.startsWith("/admin")}
                >
                  <MenuItem
                    icon={
                      <Tooltip
                        title={t("drawer.userAdmin")}
                        placement="right"
                        disableHoverListener={!isCollapsed}
                      >
                        <SupervisorAccountIcon
                          sx={{ fontSize: 18, color: TOKENS.textMuted }}
                        />
                      </Tooltip>
                    }
                    style={{ fontSize: "14px", marginLeft: "8px" , fontWeight:700 }}
                    component={<Link to="/admin-page" />}
                    active={isItemActive("/admin-page")}
                  >
                    <Typography sx={{ ml: 0.75, fontSize: "14px" , fontWeight:700 }}>
                      {t("drawer.userAdmin")}
                    </Typography>
                  </MenuItem>
                  <MenuItem
                    icon={
                      <Tooltip
                        title={t("drawer.batchAdmin")}
                        placement="right"
                        disableHoverListener={!isCollapsed}
                      >
                        <BatchPredictionIcon
                          sx={{ fontSize: 18, color: TOKENS.textMuted }}
                        />
                      </Tooltip>
                    }
                    style={{ fontSize: "14px", marginLeft: "8px" , fontWeight:700 }}
                    component={<Link to="/batch-management" />}
                    active={isItemActive("/batch-management")}
                  >
                    <Typography sx={{ ml: 0.75, fontSize: "14px" , fontWeight:700 }}>
                    {t("drawer.batchAdmin")}
                    </Typography>
                  </MenuItem>
                  <MenuItem
                    icon={
                      <Tooltip
                        title={t("drawer.creditAdmin")}
                        placement="right"
                        disableHoverListener={!isCollapsed}
                      >
                        <AccountBalanceWalletIcon
                          sx={{ fontSize: 18, color: TOKENS.textMuted }}
                        />
                      </Tooltip>
                    }
                    style={{ fontSize: "14px", marginLeft: "8px" }}
                    component={<Link to="/credit-management" />}
                    active={isItemActive("/credit-management")}
                  >
                    <Typography sx={{ ml: 0.75, fontSize: "14px" , fontWeight:700 }}>
                    {t("drawer.creditAdmin")}
                    </Typography>
                  </MenuItem>
                </SubMenu>
              )}

              {/* Navigation */}
              <SectionLabel label={t("drawer.navigation")} />
              <MenuItem
                icon={
                  <Tooltip
                    title={t("drawer.manual")}
                    placement="right"
                    disableHoverListener={!isCollapsed}
                  >
                    <SummarizeIcon
                      sx={{ fontSize: 20, color: TOKENS.textMuted }}
                    />
                  </Tooltip>
                }
                style={{ fontSize: "14px", fontWeight:700 }}
                component={<Link to="/manual" />}
                active={isItemActive("/manual")}
              >
                {!isCollapsed && t("drawer.manual")}
              </MenuItem>

              <MenuItem
                icon={
                  <Tooltip
                    title={t("drawer.dashboard")}
                    placement="right"
                    disableHoverListener={!isCollapsed}
                  >
                    <SpaceDashboardTwoToneIcon
                      sx={{ fontSize: 20, color: TOKENS.textMuted }}
                    />
                  </Tooltip>
                }
                style={{ fontSize: "14px", fontWeight:700 }}
                component={<Link to="/dashboard" />}
                active={isItemActive("/dashboard")}
              >
                {!isCollapsed && t("drawer.dashboard")}
              </MenuItem>
                            <MenuItem
                icon={
                  <Tooltip
                    title={t("drawer.faq")}
                    placement="right"
                    disableHoverListener={!isCollapsed}
                  >
                    <HelpOutlineIcon
                      sx={{ fontSize: 20, color: TOKENS.textMuted }}
                    />
                  </Tooltip>
                }
                style={{ fontSize: "14px", fontWeight:700 }}
                component={<Link to="/faq" />}
                active={isItemActive("/faq")}
              >
                {!isCollapsed && t("drawer.faq")}
              </MenuItem>
              <SectionLabel label={t("drawer.tools")} />
              <MenuItem
                icon={
                  <Tooltip
                    title="Biomarker"
                    placement="right"
                    disableHoverListener={!isCollapsed}
                  >
                    <BiotechIcon sx={{ fontSize: 20, color: TOKENS.textMuted }} />
                  </Tooltip>
                }
                style={{ fontSize: "14px", fontWeight: 700 }}
                component={<Link to="/biomarker" />}
                onClick={handleBiomarkerMenuClick}
                active={isBiomarkerToolActive()}
              >
                {!isCollapsed && "Biomarker"}
              </MenuItem>
              {/* <MenuItem
                icon={
                  <Tooltip
                    title={'MDTR'}
                    placement="right"
                    disableHoverListener={!isCollapsed}
                  >
                    <SettingsAccessibilityIcon
                      sx={{ fontSize: 20, color: TOKENS.textMuted }}
                    />
                  </Tooltip>
                }
                style={{ fontSize: "14px", fontWeight:700 }}
                component={<Link to="/mdtr" />}
                onClick={handleMdtrMenuClick}
                active={isMdtrToolActive()}
              >
                {!isCollapsed && 'MDTR'}
              </MenuItem> */}
              <MenuItem
                icon={
                  <Tooltip
                    title={t("drawer.drugResponse")}
                    placement="right"
                    disableHoverListener={!isCollapsed}
                  >
                    <MonitorHeartIcon
                      sx={{ fontSize: 20, color: TOKENS.textMuted }}
                    />
                  </Tooltip>
                }
                style={{ fontSize: "14px", fontWeight:700 }}
                component={<Link to="/drug-response" />}
                onClick={handleDrugResponseMenuClick}
                active={isDrugResponseToolActive()}
              >
                {!isCollapsed && t("drawer.drugResponse")}
              </MenuItem>
              <MenuItem
                icon={
                  <Tooltip
                    title={t("drawer.drugToxicity")}
                    placement="right"
                    disableHoverListener={!isCollapsed}
                  >
                    <ScienceIcon
                      sx={{ fontSize: 20, color: TOKENS.textMuted }}
                    />
                  </Tooltip>
                }
                style={{ fontSize: "14px", fontWeight: 700 }}
                component={<Link to="/drug-toxicity" />}
                onClick={handleDrugToxicityMenuClick}
                active={isDrugToxicityToolActive()}
              >
                {!isCollapsed && t("drawer.drugToxicity")}
              </MenuItem>
              <SubMenu
                label={!isCollapsed ? t("drawer.drugGeneration") : ""}
                icon={
                  <Tooltip
                    title={t("drawer.drugGeneration")}
                    placement="right"
                    disableHoverListener={!isCollapsed}
                  >
                    <HubIcon sx={{ fontSize: 20, color: TOKENS.textMuted }} />
                  </Tooltip>
                }
                style={{ fontSize: "14px", fontWeight: 700 }}
                active={isPPIToolActive() || isKinaseToolActive()}
              >
                <MenuItem
                  icon={
                    <Tooltip
                      title={t("PPI.title")}
                      placement="right"
                      disableHoverListener={!isCollapsed}
                    >
                      <HubIcon sx={{ fontSize: 18, color: TOKENS.textMuted }} />
                    </Tooltip>
                  }
                  style={{
                    fontSize: "14px",
                    marginLeft: "8px",
                    fontWeight: 700,
                    opacity: isCreatingLibrary ? 0.5 : 1,
                    pointerEvents: isCreatingLibrary ? "none" : "auto",
                  }}
                  onClick={handlePPIMenuClick}
                  active={isPPIToolActive()}
                >
                  {!isCollapsed && t("PPI.title")}
                </MenuItem>
                <MenuItem
                  icon={
                    <Tooltip
                      title={t("Kinase.title")}
                      placement="right"
                      disableHoverListener={!isCollapsed}
                    >
                      <BiotechIcon
                        sx={{ fontSize: 18, color: TOKENS.textMuted }}
                      />
                    </Tooltip>
                  }
                  style={{
                    fontSize: "14px",
                    marginLeft: "8px",
                    fontWeight: 700,
                    opacity: isCreatingLibrary ? 0.5 : 1,
                    pointerEvents: isCreatingLibrary ? "none" : "auto",
                  }}
                  onClick={handleKinaseMenuClick}
                  active={isKinaseToolActive()}
                >
                  {!isCollapsed && t("Kinase.title")}
                </MenuItem>
              </SubMenu>

              {/* Prompt history 목록 */}
              {itemList && itemList.length > 0 && (
                <>
                  <SectionLabel label={t("drawer.history")} />
                  {itemList.map((item: any, idx: number) => {
                    const active = isItemActive(`/prompt/${item}`);
                    return (
                      <MenuItem
                        key={idx}
                        style={{ fontSize: "14px" }}
                        icon={
                          <ChatIcon
                            sx={{ fontSize: 18, color: TOKENS.textMuted }}
                          />
                        }
                        component={<Link to={`/prompt/${item}`} />}
                        active={active}
                        suffix={
                          !isCollapsed && hoveredItem === item ? (
                            <IconButton
                              size="small"
                              onClick={(e) => handleDeleteIconClick(library, e)}
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: "6px",
                                color: TOKENS.textMuted,
                                "&:hover": {
                                  background: "rgba(239,68,68,0.08)",
                                  color: TOKENS.danger,
                                },
                              }}
                            >
                              <DeleteForeverIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          ) : null
                        }
                        onMouseEnter={() => setHoveredItem(item)}
                        onMouseLeave={() => setHoveredItem(null)}
                      >
                        {!isCollapsed && (
                          <Typography
                            noWrap
                            sx={{
                              fontWeight: active ? 600 : 400,
                              fontSize: "14px",
                            }}
                          >
                            {item}
                          </Typography>
                        )}
                      </MenuItem>
                    );
                  })}
                </>
              )}

              {/* 워크스페이스 목록*/}
              <SectionLabel label={t("drawer.workspaces")} />

              {listLibrary &&
              Array.isArray(listLibrary) &&
              listLibrary.length > 0
                ? [...listLibrary]
                    .sort((a: any, b: any) =>
                      (b.created_at || "").localeCompare(a.created_at || "")
                    )
                    .map((lib: any, idx: number) => {
                      const wsName = lib.library_name;
                      const wfName = lib.workflow_name;
                      const wfPath = getWorkflowPath(wfName, lib.workflow_type);
                      const isActive =
                        location.pathname === `/${wfPath}/${wsName}` ||
                        location.pathname === `/${wfPath}/${wsName}/round1` ||
                        location.pathname === `/${wfPath}/${wsName}/round2` ||
                        location.pathname === `/${wfPath}/${wsName}/round3` ||
                        location.pathname.startsWith(`/${wfPath}/${wsName}/`);
                      // Drug Response/Drug Toxicity는 라운드 서브메뉴 없이 단일 워크스페이스 항목으로 표시
                      const hasRounds =
                        hasRoundItems(wsName) &&
                        !isDrugResponseWorkflow(wfName) && !isDrugToxicityWorkflow(wfName) &&
                        !isMdtrWorkflow(wfName) && !isBiomarkerWorkflow(wfName);
                      const cfg =
                        WORKFLOW_CONFIG[normalizeWorkflowName(wfName)];

                      return (
                        <div
                          key={`ws-${idx}`}
                          ref={(el) => {
                            if (el) libraryItemRefs.current.set(wsName, el);
                            else libraryItemRefs.current.delete(wsName);
                          }}
                        >
                          {hasRounds &&
                          !isCollapsed &&
                          !isDrugResponsePath(wfPath) ? (
                            <div
                              ref={(el) => {
                                if (el) subMenuRefs.current.set(wsName, el);
                                else subMenuRefs.current.delete(wsName);
                              }}
                            >
                              <SubMenu
                                label={
                                  <Tooltip
                                    title={wsName}
                                    placement="right"
                                    enterDelay={600}
                                    slotProps={{
                                      tooltip: {
                                        sx: {
                                          backgroundColor: TOKENS.text,
                                          fontSize: "0.8rem",
                                          borderRadius: "8px",
                                        },
                                      },
                                      arrow: { sx: { color: TOKENS.text } },
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        width: "100%",
                                        position: "relative",
                                      }}
                                      onMouseEnter={() =>
                                        preloadRouteForPath(
                                          buildLibraryTargetPath({
                                            workflowName: wfName,
                                            workflowType: lib.workflow_type,
                                            libraryName: wsName,
                                            roundNum: 1,
                                          })
                                        )
                                      }
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setOpenedLibrary(
                                          openedLibrary !== wsName
                                            ? wsName
                                            : null
                                        );
                                        dispatch(setLibrary(wsName));
                                        navigate(
                                          buildLibraryTargetPath({
                                            workflowName: wfName,
                                            workflowType: lib.workflow_type,
                                            libraryName: wsName,
                                            roundNum: 1,
                                          }),
                                          { replace: true }
                                        );
                                      }}
                                    >
<Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
  <Typography noWrap sx={{ fontWeight: isActive ? 600 : 400, fontSize: "14px" }}>
    {wsName}
  </Typography>
</Box>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          ml: "auto",
                                          pl: 1,
                                          width: 60,
                                          justifyContent: "flex-end",
                                          visibility: "hidden",
                                          opacity: 0,
                                          pointerEvents: "none",
                                          transition: "opacity 0.15s ease",
                                          ".ps-menu-button:hover &": {
                                            visibility: "visible",
                                            opacity: 1,
                                            pointerEvents: "auto",
                                          },
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                          <IconButton
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDialogOpen(
                                                wsName,
                                                "editLibrary"
                                              );
                                            }}
                                            sx={{
                                              width: 26,
                                              height: 26,
                                              borderRadius: "7px",
                                              "&:hover": {
                                                background: TOKENS.accentLight,
                                                color: TOKENS.accent,
                                              },
                                            }}
                                          >
                                            <EditNoteIcon
                                              sx={{ fontSize: 18 }}
                                            />
                                          </IconButton>
                                          <IconButton
                                            size="small"
                                            onClick={(e) =>
                                              handleDeleteIconClick(
                                                lib.library_name,
                                                e
                                              )
                                            }
                                            sx={{
                                              width: 26,
                                              height: 26,
                                              borderRadius: "7px",
                                              "&:hover": {
                                                background:
                                                  "rgba(239,68,68,0.08)",
                                                color: TOKENS.danger,
                                              },
                                            }}
                                          >
                                            <DeleteForeverIcon
                                              sx={{ fontSize: 18 }}
                                            />
                                          </IconButton>
                                      </Box>
                                    </Box>
                                  </Tooltip>
                                }
                                icon={getWorkflowIcon(wfName)}
                                active={isActive}
                                open={openedLibrary === wsName}
                              >
                                {/* 라운드 항목 들여쓰기 */}
                                {getLibraryRounds(wsName).map((rn: any) => (
                                  <MenuItem
                                    key={`r-${rn}`}
                                    style={{
                                      marginLeft: 24,
                                      fontSize: "13.5px",
                                      height: "40px",
                                      minHeight: "40px",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                    icon={
                                      <ChangeCircleIcon
                                        sx={{
                                          fontSize: 16,
                                          color: cfg?.color ?? TOKENS.textMuted,
                                        }}
                                      />
                                    }
                                    component={
                                      <Link
                                        to={buildLibraryTargetPath({
                                          workflowName: wfName,
                                          workflowType: lib.workflow_type,
                                          libraryName: wsName,
                                          roundNum: parseInt(rn, 10) || 1,
                                        })}
                                      />
                                    }
                                    active={
                                      location.pathname ===
                                      buildLibraryTargetPath({
                                        workflowName: wfName,
                                        workflowType: lib.workflow_type,
                                        libraryName: wsName,
                                        roundNum: parseInt(rn, 10) || 1,
                                      })
                                    }
                                    onClick={() =>
                                      handleLibraryClick(
                                        wsName,
                                        wfName,
                                        lib.workflow_type,
                                        parseInt(rn, 10) || 1
                                      )
                                    }
                                  >
                                    <Typography
                                      noWrap
                                      sx={{
                                        fontSize: "13.5px",
                                        fontWeight:
                                          location.pathname ===
                                          `/${wfPath}/${wsName}/round${rn}`
                                            ? 600
                                            : 400,
                                      }}
                                    >
                                      {t("drawer.round", { round: rn })}
                                    </Typography>
                                  </MenuItem>
                                ))}
                                <MenuItem
                                  style={{
                                    marginLeft: 24,
                                    fontSize: "13.5px",
                                    height: "40px",
                                    minHeight: "40px",
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                  icon={
                                    <AddCircleIcon
                                      sx={{
                                        fontSize: 16,
                                        color: cfg?.color ?? TOKENS.textMuted,
                                      }}
                                    />
                                  }
                                  onClick={(e) =>
                                    handleNextRoundClick(wsName, e)
                                  }
                                >
                                  {!isCollapsed && (
                                    <Typography
                                      sx={{
                                        fontSize: "13.5px",
                                        color: TOKENS.textSub,
                                      }}
                                    >
                                      {t("drawer.nextRound")}
                                    </Typography>
                                  )}
                                </MenuItem>
                              </SubMenu>
                            </div>
                          ) : (
                            <MenuItem
                              style={{ fontSize: "14px" }}
                              icon={getWorkflowIcon(wfName)}
                              component={
                                <Link
                                  to={buildLibraryTargetPath({
                                    workflowName: wfName,
                                    workflowType: lib.workflow_type,
                                    libraryName: wsName,
                                    roundNum: 1,
                                  })}
                                />
                              }
                              active={isActive}
                              onClick={() =>
                                handleLibraryClick(
                                  wsName,
                                  wfName,
                                  lib.workflow_type,
                                  1
                                )
                              }
                              suffix={
                                !isCollapsed ? (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexShrink: 0,
                                      pl: 0.5,
                                      width: 60,
                                      justifyContent: "flex-end",
                                      visibility: "hidden",
                                      opacity: 0,
                                      pointerEvents: "none",
                                      transition: "opacity 0.15s ease",
                                      ".ps-menu-button:hover &": {
                                        visibility: "visible",
                                        opacity: 1,
                                        pointerEvents: "auto",
                                      },
                                    }}
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleDialogOpen(
                                          lib.library_name,
                                          "editLibrary"
                                        );
                                      }}
                                      sx={{
                                        width: 26,
                                        height: 26,
                                        borderRadius: "7px",
                                        "&:hover": {
                                          background: TOKENS.accentLight,
                                          color: TOKENS.accent,
                                        },
                                      }}
                                    >
                                      <EditNoteIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleDialogOpen(
                                          lib.library_name,
                                          "library"
                                        );
                                      }}
                                      sx={{
                                        width: 26,
                                        height: 26,
                                        borderRadius: "7px",
                                        "&:hover": {
                                          background: "rgba(239,68,68,0.08)",
                                          color: TOKENS.danger,
                                        },
                                      }}
                                    >
                                      <DeleteForeverIcon
                                        sx={{ fontSize: 14 }}
                                      />
                                    </IconButton>
                                  </Box>
                                ) : null
                              }
                            >
                              {!isCollapsed && (
                                <Tooltip
                                  title={wsName}
                                  placement="right"
                                  enterDelay={600}
                                  slotProps={{
                                    tooltip: {
                                      sx: {
                                        backgroundColor: TOKENS.text,
                                        fontSize: "0.8rem",
                                        borderRadius: "8px",
                                      },
                                    },
                                    arrow: { sx: { color: TOKENS.text } },
                                  }}
                                >
                                  <Typography
                                    noWrap
                                    sx={{
                                      fontWeight: isActive ? 600 : 400,
                                      fontSize: "14px",
                                    }}
                                  >
                                    {truncateText(wsName, 13)}
                                  </Typography>
                                </Tooltip>
                              )}
                            </MenuItem>
                          )}
                        </div>
                      );
                    })
                : !isCollapsed && (
                    <Box
                      sx={{
                        px: 2.5,
                        py: 2,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: "10px",
                          background: TOKENS.bgMuted,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <FolderIcon
                          sx={{ fontSize: 20, color: TOKENS.textMuted }}
                        />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "0.78rem",
                          color: TOKENS.textMuted,
                          textAlign: "center",
                          lineHeight: 1.5,
                        }}
                      >
                        {t("drawer.noWorkspacesYet")}
                      </Typography>
                    </Box>
                  )}

              <Box sx={{ height: 16 }} />
            </Menu>
          </Box>
        </Sidebar>

        {/* ══ DELETE / RENAME DIALOG ══ */}
        <Dialog
          disableScrollLock
          disableAutoFocus
          open={isOpen}
          onKeyDown={handleWorkspaceDialogEnter}
          onClose={
            deleteLoading && dialogType === "library"
              ? undefined
              : handleDialogClose
          }
          keepMounted={false}
          PaperProps={{
            sx: {
              borderRadius: "18px",
              overflow: "hidden",
              boxShadow:
                "0 24px 60px -12px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.04)",
              background: "#fff",
              minWidth: "440px",
              maxWidth: "480px",
            },
          }}
          BackdropProps={BACKDROP_PROPS}
        >
          {dialogType === "editLibrary" ? (
            <>
              <Box sx={{ px: 3.5, pt: 3.5, pb: 2.5 }}>
                <IconBadge
                  bgColor="#eff6ff"
                  borderColor="#bfdbfe"
                >
                  <EditIcon sx={{ color: "#3b82f6", fontSize: "20px" }} />
                </IconBadge>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "1rem",
                    color: TOKENS.text,
                    letterSpacing: "-0.02em",
                    lineHeight: 1.3,
                    mb: 0.4,
                  }}
                >
                  {t("PPI.modal.renameLibrary")}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    color: TOKENS.textMuted,
                    lineHeight: 1.6,
                  }}
                >
                  {t("PPI.modal.renameLibraryNotice")}
                </Typography>
              </Box>
              <ModalDivider />
              <DialogContent sx={{ px: 3.5, py: 2.5 }}>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    color: TOKENS.textMuted,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.07em",
                    mb: 1,
                  }}
                >
                  {t("PPI.modal.now")} {t("drawer.workspace")}
                </Typography>
                <Box
                  sx={{
                    px: 2,
                    py: 1.25,
                    borderRadius: "10px",
                    background: TOKENS.bgSubtle,
                    border: `1px solid ${TOKENS.borderStrong}`,
                    mb: 2,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      color: TOKENS.text,
                    }}
                  >
                    {itemName}
                  </Typography>
                </Box>
                <TextField
                  autoFocus
                  fullWidth
                  label={t("PPI.modal.newWorkSpaceName")}
                  variant="outlined"
                  value={editedLibraryName}
                  onChange={(e) => {
                    setEditedLibraryName(sanitizeWorkspaceName(e.target.value));
                    if (renameError) setRenameError("");
                  }}
                  error={Boolean(renameError)}
                  helperText={renameError || " "}
                  disabled={renameLoading}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      backgroundColor: TOKENS.bgSubtle,
                      fontSize: "0.85rem",
                    },
                    "& .MuiInputLabel-root": { fontSize: "0.85rem" },
                    "& .MuiFormHelperText-root": {
                      mx: 0.5,
                      mt: 0.75,
                      fontSize: "0.75rem",
                    },
                  }}
                />
              </DialogContent>
              <ModalDivider />
              <DialogActions sx={{ px: 3.5, py: 2.5, gap: 1.5 }}>
                <Button
                  variant="outlined"
                  onClick={handleDialogClose}
                  disabled={renameLoading}
                  sx={cancelBtnSx}
                >
                  {t("PPI.cancel")}
                </Button>
                <Button
                  variant="contained"
                  onClick={() =>
                    handleRenameLibrary(itemName, editedLibraryName)
                  }
                  disabled={
                    !editedLibraryName.trim() ||
                    editedLibraryName === itemName ||
                    renameLoading
                  }
                  startIcon={
                    renameLoading ? (
                      <CircularProgress size={13} color="inherit" />
                    ) : (
                      <CheckCircleIcon sx={{ fontSize: "15px !important" }} />
                    )
                  }
                  sx={primaryBtnSx}
                >
                  {renameLoading
                    ? t("PPI.modal.renaming")
                    : t("PPI.modal.change")}
                </Button>
              </DialogActions>
            </>
          ) : (
            <>
              <Box sx={{ px: 3.5, pt: 3.5, pb: 2.5 }}>
                <IconBadge
                  bgColor="#fef2f2"
                  borderColor="#fecaca"
                >
                  <WarningIcon
                    sx={{ color: TOKENS.danger, fontSize: "20px" }}
                  />
                </IconBadge>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "1rem",
                    color: TOKENS.text,
                    letterSpacing: "-0.02em",
                    lineHeight: 1.3,
                    mb: 0.4,
                  }}
                >
                  {dialogType === "chat"
                    ? t("drawer.chatDelete")
                    : t("PPI.modal.deleteLibrary")}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    color: TOKENS.textMuted,
                    lineHeight: 1.6,
                  }}
                >
                  {t("PPI.modal.noChangeThisDelete")}
                </Typography>
              </Box>
              <ModalDivider />
              <DialogContent sx={{ px: 3.5, py: 2.5 }}>
                <Box
                  sx={{
                    backgroundColor: "#fef2f2",
                    borderRadius: "10px",
                    padding: "14px 18px",
                    border: "1px solid #fecaca",
                    mb: 1.5,
                  }}
                >
                  <Typography
                    sx={{
                      color: "#7f1d1d",
                      fontWeight: 700,
                      fontSize: "0.875rem",
                      wordBreak: "break-word",
                      mb: 0.4,
                    }}
                  >
                    "{itemName}"
                  </Typography>
                  <Typography
                    sx={{
                      color: "#991b1b",
                      fontSize: "0.78rem",
                      fontWeight: 500,
                      lineHeight: 1.55,
                    }}
                  >
                    {dialogType === "chat"
                      ? t("drawer.chatDeleteNotice")
                      : t("PPI.modal.deleteLibraryNotice")}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: TOKENS.textMuted,
                    fontStyle: "italic",
                  }}
                >
                  {t("PPI.modal.notice")}
                </Typography>
              </DialogContent>
              <ModalDivider />
              <DialogActions sx={{ px: 3.5, py: 2.5, gap: 1.5 }}>
                <Button
                  variant="outlined"
                  onClick={handleDialogClose}
                  disabled={deleteLoading && dialogType === "library"}
                  sx={cancelBtnSx}
                >
                  {t("PPI.cancel")}
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    if (dialogType === "chat") {
                      deleteChat(itemName);
                      if (`/prompt/${itemName}` === location.pathname)
                        navigate("/prompt");
                      handleDialogClose();
                    } else handleDeleteLibrary(itemName);
                  }}
                  disabled={deleteLoading && dialogType === "library"}
                  startIcon={
                    deleteLoading && dialogType === "library" ? (
                      <CircularProgress size={13} color="inherit" />
                    ) : (
                      <DeleteIcon sx={{ fontSize: "15px !important" }} />
                    )
                  }
                  sx={dangerBtnSx}
                >
                  {deleteLoading && dialogType === "library"
                    ? t("PPI.modal.deleteing")
                    : t("drawer.delete")}
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Box>
      )}

      {/* ══ ASSAY CHOICE MODAL ══ */}
      <Dialog
        disableScrollLock
        disableAutoFocus
        open={showAssayChoiceModal}
        onClose={handleAssayChoiceModalClose}
        PaperProps={{
          sx: {
            borderRadius: "18px",
            overflow: "hidden",
            boxShadow:
              "0 24px 60px -12px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.04)",
            background: "#fff",
            minWidth: "440px",
            maxWidth: "480px"
          },
        }}
        BackdropProps={BACKDROP_PROPS}
      >
        <Box sx={{ px: 3.5, pt: 3.5, pb: 2.5 }}>
          <IconBadge
            bgColor="#eff6ff"
            borderColor="#bfdbfe"
          >
            <FolderIcon sx={{ color: "#3b82f6", fontSize: "20px" }} />
          </IconBadge>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1rem",
              color: TOKENS.text,
              letterSpacing: "-0.02em",
              lineHeight: 1.3,
              mb: 0.4,
            }}
          >
            {userChoiceSelectTool} — Assay File
          </Typography>
          <Typography
            sx={{
              fontSize: "0.8rem",
              color: TOKENS.textMuted,
              lineHeight: 1.6,
            }}
          >
            {t("PPI.modal.selectOptions")}
          </Typography>
        </Box>
        <ModalDivider />
        <DialogContent sx={{ px: 3.5, py: 2.5 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            <ChoiceCard
              onClick={handleWithAssayFile}
              hoverBorderColor={TOKENS.accent}
              hoverBgColor={TOKENS.accentLight}
              iconBg={`linear-gradient(135deg, ${TOKENS.accent} 0%, #6b8dd6 100%)`}
              Icon={FolderIcon}
              title="Have assay data"
              description={t("PPI.modal.assayWorkspace")}
            />
            <ChoiceCard
              onClick={handleNoAssayFile}
              hoverBorderColor={TOKENS.warning}
              hoverBgColor="#fffbeb"
              iconBg="linear-gradient(135deg, #f59e0b 0%, #f7ac34 100%)"
              Icon={FolderOffIcon}
              title="No assay data"
              description={t("PPI.modal.quickStart")}
            />
          </Box>
        </DialogContent>
        <ModalDivider />
        <DialogActions sx={{ px: 3.5, py: 2.5, gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={handleAssayChoiceModalClose}
            sx={cancelBtnSx}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══ DRUG RESPONSE MODAL ══ */}
      <Dialog
        disableScrollLock
        disableAutoFocus
        open={showDrugResponseModal}
        onClose={handleDrugResponseModalClose}
        PaperProps={{
          sx: {
            borderRadius: "18px",
            overflow: "hidden",
            boxShadow:
              "0 24px 60px -12px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.04)",
            background: "#fff",
            minWidth: "440px",
            maxWidth: "480px"
          },
        }}
        BackdropProps={BACKDROP_PROPS}
      >
        <Box sx={{ px: 3.5, pt: 3.5, pb: 2.5, position: "relative" }}>
          <IconButton
            onClick={handleDrugResponseModalClose}
            sx={{
              position: "absolute",
              top: 14,
              right: 14,
              width: 28,
              height: 28,
              borderRadius: "8px",
              color: TOKENS.textMuted,
              "&:hover": { color: TOKENS.textSub, background: TOKENS.bgMuted },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <IconBadge
            bgColor="#ecfdf5"
            borderColor="#a7f3d0"
          >
            <MonitorHeartIcon
              sx={{ color: TOKENS.success, fontSize: "20px" }}
            />
          </IconBadge>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1rem",
              color: TOKENS.text,
              letterSpacing: "-0.02em",
              lineHeight: 1.3,
              mb: 0.4,
            }}
          >
            {userChoiceSelectTool} — Select Mode
          </Typography>
          <Typography
            sx={{
              fontSize: "0.8rem",
              color: TOKENS.textMuted,
              lineHeight: 1.6,
            }}
          >
            {t("PPI.modal.selectOptions")}
          </Typography>
        </Box>
        <ModalDivider />
        <DialogContent sx={{ px: 3.5, py: 2.5 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            <ChoiceCard
              onClick={() => {
                dispatch(setUserDrugResponseChoiceTool("Clinical"));
                dispatch(setUserChoiceToolType("clinical"));
                handleNoAssayFile();
              }}
              hoverBorderColor={TOKENS.accent}
              hoverBgColor={TOKENS.accentLight}
              iconBg={`linear-gradient(135deg, ${TOKENS.accent} 0%, #6b8dd6 100%)`}
              Icon={BarChartIcon}
              title="Clinical"
              description={t("PPI.modal.assayWorkspace")}
            />
            <ChoiceCard
              onClick={() => {
                dispatch(setUserDrugResponseChoiceTool("Preclinical"));
                dispatch(setUserChoiceToolType("preclinical"));
                handleNoAssayFile();
              }}
              hoverBorderColor={TOKENS.success}
              hoverBgColor="#f0fdf4"
              iconBg="linear-gradient(135deg, #10b981 0%, #059669 100%)"
              Icon={BiotechIcon}
              title="Preclinical"
              description={t("PPI.modal.quickStart")}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* ══ DRUG TOXICITY MODAL ══ */}
      <Dialog
        disableScrollLock
        disableAutoFocus
        open={showDrugToxicityModal}
        onClose={handleDrugToxicityModalClose}
        PaperProps={{
          sx: {
            borderRadius: "18px",
            overflow: "hidden",
            boxShadow:
              "0 24px 60px -12px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.04)",
            background: "#fff",
            minWidth: "440px",
            maxWidth: "480px"
          },
        }}
        BackdropProps={BACKDROP_PROPS}
      >
        <Box sx={{ px: 3.5, pt: 3.5, pb: 2.5, position: "relative" }}>
          <IconButton
            onClick={handleDrugToxicityModalClose}
            sx={{
              position: "absolute",
              top: 14,
              right: 14,
              width: 28,
              height: 28,
              borderRadius: "8px",
              color: TOKENS.textMuted,
              "&:hover": { color: TOKENS.textSub, background: TOKENS.bgMuted },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <IconBadge bgColor="#fff7ed" borderColor="#fed7aa">
            <ScienceIcon sx={{ color: TOKENS.warning, fontSize: "20px" }} />
          </IconBadge>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1rem",
              color: TOKENS.text,
              letterSpacing: "-0.02em",
              lineHeight: 1.3,
              mb: 0.4,
            }}
          >
            {t("drugToxicityToolSelectModal.title")}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.8rem",
              color: TOKENS.textMuted,
              lineHeight: 1.6,
            }}
          >
            {t("PPI.modal.selectOptions")}
          </Typography>
        </Box>
        <ModalDivider />
        <DialogContent sx={{ px: 3.5, py: 2.5 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.1 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 800, color: TOKENS.textMuted }}>
              {t("drugToxicityToolSelectModal.structureBasedSectionLabel")}
            </Typography>
            <ChoiceCard
              onClick={() => {
                dispatch(setUserChoiceTool("Drug Toxicity"));
                dispatch(setUserChoiceToolType("fatetox"));
                setSelectedDrugToxicityType("fatetox");
                handleNoAssayFile();
              }}
              hoverBorderColor={TOKENS.success}
              hoverBgColor="#f0fdf4"
              iconBg="linear-gradient(135deg, #10b981 0%, #059669 100%)"
              Icon={BiotechIcon}
              title={t("drugToxicityToolSelectModal.fatetoxTitle")}
              description={t("drugToxicityToolSelectModal.fatetoxDescription")}
            />
            <Typography sx={{ mt: 0.6, fontSize: 11, fontWeight: 800, color: TOKENS.textMuted }}>
              {t("drugToxicityToolSelectModal.transcriptomicsSectionLabel")}
            </Typography>
            <ChoiceCard
              onClick={() => {
                dispatch(setUserChoiceTool("Drug Toxicity"));
                dispatch(setUserChoiceToolType("mdtr"));
                setSelectedDrugToxicityType("mdtr");
                handleNoAssayFile();
              }}
              hoverBorderColor={TOKENS.accent}
              hoverBgColor={TOKENS.accentLight}
              iconBg={`linear-gradient(135deg, ${TOKENS.accent} 0%, #6b8dd6 100%)`}
              Icon={HubIcon}
              title={t("drugToxicityToolSelectModal.mdtrTitle")}
              description={t("drugToxicityToolSelectModal.mdtrDescription")}
            />
            <Typography sx={{ mt: 0.6, fontSize: 11, fontWeight: 800, color: TOKENS.textMuted }}>
              {t("drugToxicityToolSelectModal.inVivoSectionLabel")}
            </Typography>
            <ChoiceCard
              onClick={() => {
                dispatch(setUserChoiceTool("Drug Toxicity"));
                dispatch(setUserChoiceToolType("animal"));
                setSelectedDrugToxicityType("animal");
                handleNoAssayFile();
              }}
              hoverBorderColor={TOKENS.warning}
              hoverBgColor="#fff7ed"
              iconBg="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
              Icon={ScienceIcon}
              title={t("drugToxicityToolSelectModal.animalTitle")}
              description={t("drugToxicityToolSelectModal.animalDescription")}
            />
          </Box>
        </DialogContent>
      </Dialog>

      

      {showPPIModal && (
        // 워크스페이스 생성 모달. 선택된 툴과 assay 상태에 맞춰 새 라이브러리를 생성.
        <MakeWorkSpace
          key={modalKey}
          isOpen={showPPIModal}
          onNextStep={handleModalClose}
        />
      )}
      {/* 사용자 메뉴. 아바타 메뉴 열림 상태는 appbar slice에서 관리. */}
      <AvatarMenu />

      {/* 실행 중 확인 Dialog. 실행 중인 워크스페이스의 삭제/이름변경을 보호. */}
      <Dialog
        disableScrollLock
        disableAutoFocus
        open={runningModal && modalType === "running-checking"}
        onClose={handleCancelForceDelete}
        keepMounted={false}
        PaperProps={{
          sx: {
            borderRadius: "18px",
            overflow: "hidden",
            boxShadow:
              "0 24px 60px -12px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.04)",
            background: "#fff",
            minWidth: "440px",
            maxWidth: "480px"
          },
        }}
        BackdropProps={BACKDROP_PROPS}
      >
        <Box sx={{ px: 3.5, pt: 3.5, pb: 2.5 }}>
          <IconBadge
            bgColor="#fffbeb"
            borderColor="#fde68a"
          >
            <WarningIcon sx={{ color: TOKENS.warning, fontSize: "20px" }} />
          </IconBadge>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1rem",
              color: TOKENS.text,
              letterSpacing: "-0.02em",
              lineHeight: 1.3,
              mb: 0.4,
            }}
          >
            Task in Progress
          </Typography>
          <Typography
            sx={{
              fontSize: "0.8rem",
              color: TOKENS.textMuted,
              lineHeight: 1.6,
            }}
          >
            {runningCheckAction === "rename"
              ? "This workspace cannot be renamed while a task is running."
              : "A task is currently running in the background."}
          </Typography>
        </Box>
        <ModalDivider />
        <DialogContent sx={{ px: 3.5, py: 2.5 }}>
          <Box
            sx={{
              backgroundColor: "#fffbeb",
              borderRadius: "10px",
              padding: "14px 18px",
              border: "1px solid #fde68a",
              mb: 1.5,
            }}
          >
            <Typography
              sx={{
                color: "#92400e",
                fontWeight: 700,
                fontSize: "0.875rem",
                wordBreak: "break-word",
                mb: 0.4,
              }}
            >
              "{itemName}"
            </Typography>
            <Typography
              sx={{
                color: "#a16207",
                fontSize: "0.78rem",
                fontWeight: 500,
                lineHeight: 1.6,
              }}
            >
              {runningCheckAction === "rename"
                ? "Please try again after the running workflow is complete."
                : "Deleting this workspace will stop all running tasks permanently."}
            </Typography>
          </Box>
          {runningCheckAction === "delete" && (
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: TOKENS.textMuted,
                fontStyle: "italic",
              }}
            >
              This operation is irreversible.
            </Typography>
          )}
        </DialogContent>
        <ModalDivider />
        <DialogActions sx={{ px: 3.5, py: 2.5, gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={handleCancelForceDelete}
            sx={cancelBtnSx}
          >
            {runningCheckAction === "rename" ? "OK" : "Cancel"}
          </Button>
          {runningCheckAction === "delete" && (
            <Button
              variant="contained"
              onClick={handleForceDelete}
              disabled={deleteLoading}
              startIcon={
                deleteLoading ? (
                  <CircularProgress size={13} color="inherit" />
                ) : (
                  <DeleteIcon sx={{ fontSize: "15px !important" }} />
                )
              }
              sx={dangerBtnSx}
            >
              {deleteLoading ? "Deleting…" : "DELETE"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* 다음 라운드 이동 모달. 선택한 워크스페이스의 다음 round 생성을 처리. */}
      {showNextRoundModal && (
        <NextRoundMoveModal
          open
          onClose={() => {
            setShowNextRoundModal(false);
            setSelectedLibraryForNextRound(null);
            setNextRoundNumber(null);
          }}
          selectedLibraryForNextRound={selectedLibraryForNextRound}
          nextRoundNumber={nextRoundNumber}
        />
      )}
    </>
  );
}

export default MainDrawer;
